// The editable LaTeX pane: a lazy-loaded CodeMirror editor with debounced
// autosave (PATCH) + a Compile button. Forwards an imperative editor handle up to
// the workspace (so AI edits apply as CM transactions) and reports the selection
// (so the command bar can target a passage).
import { Suspense, lazy, useEffect, useRef, useState, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "@/components/ui";
import type { ReportCompileStatus } from "@/api/report";
import type { CodeMirrorHandle, EditorSelection } from "./codemirror-editor";

const CodeMirrorEditor = lazy(() => import("./codemirror-editor"));

const SAVE_DEBOUNCE_MS = 1000;

interface Props {
  documentKey: string;
  initialValue: string;
  compileStatus: ReportCompileStatus;
  autoCompile?: boolean;
  onSave: (value: string) => Promise<unknown>;
  onCompile: () => Promise<unknown>;
  editorRef?: Ref<CodeMirrorHandle>;
  onSelectionChange?: (sel: EditorSelection | null) => void;
}

export function LatexEditorPanel({
  documentKey,
  initialValue,
  compileStatus,
  autoCompile = false,
  onSave,
  onCompile,
  editorRef,
  onSelectionChange,
}: Props) {
  const { t } = useTranslation();
  const draft = useRef(initialValue);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    draft.current = initialValue;
    dirty.current = false;
    setSaveState("idle");
  }, [documentKey, initialValue]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const flush = async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!dirty.current) return;
    setSaveState("saving");
    await onSave(draft.current);
    dirty.current = false;
    setSaveState("saved");
    // Opt-in: recompile once the edit settles (skip if a compile is in flight).
    if (autoCompile && compileStatus !== "compiling") {
      try {
        await onCompile();
      } catch {
        /* compile errors surface via compile_status */
      }
    }
  };

  const handleChange = (value: string) => {
    draft.current = value;
    dirty.current = true;
    setSaveState("idle");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
  };

  const compiling = compileStatus === "compiling";
  const handleCompile = async () => {
    await flush();
    await onCompile();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-black/5 px-3 py-2">
        <span className="text-xs text-ink-faint">
          {saveState === "saving"
            ? t("Saving…")
            : saveState === "saved"
              ? t("Saved")
              : t("LaTeX source")}
        </span>
        <Button size="sm" onClick={handleCompile} disabled={compiling}>
          {compiling ? (
            <span className="flex items-center gap-1">
              <Spinner className="h-3 w-3" /> {t("Compiling…")}
            </span>
          ) : (
            t("Compile")
          )}
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <CodeMirrorEditor
            key={documentKey}
            ref={editorRef}
            initialValue={initialValue}
            onChange={handleChange}
            onSelectionChange={onSelectionChange}
          />
        </Suspense>
      </div>
    </div>
  );
}
